import { FC } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  useTheme
} from "@mui/material";
import { Button } from "@vilocnv/allsetra-core";
import { useTranslation } from "react-i18next";
import {
  DriveEta as DriveIcon,
  DirectionsCar as CarIcon,
  VpnKey as KeyIcon,
  CalendarToday as CalendarIcon
} from "@mui/icons-material";

interface UpdateObjectRideFormData {
  driverId?: string;
  keyId?: string;
  rideMode?: number;
  privateMileage?: number | string;
  comments?: string;
  changeReason: string;
  startAddress?: string;
  endAddress?: string;
  correctedStartAddress?: string;
  correctedEndAddress?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  formData: UpdateObjectRideFormData | null;
  driverEmail?: string;
  keyName?: string;
  rideModeLabel?: string;
}

const UpdateObjectRideConfirmationModal: FC<Props> = ({
  isOpen,
  onClose,
  onConfirm,
  formData,
  driverEmail,
  keyName,
  rideModeLabel
}) => {
  const { t } = useTranslation(["translation", "formFieldsTranslation"]);
  const theme = useTheme();

  if (!formData) return null;

  const formatPrivateMileage = (mileage: number | string | undefined) => {
    if (!mileage) return "0 km";
    return `${Number(mileage).toLocaleString()} km`;
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxHeight: "90vh"
        }
      }}
    >
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          textAlign: "center"
        }}
      >
        <Box
          sx={{
            width: "56px",
            height: "56px",
            backgroundColor: theme.palette.primary.light,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px"
          }}
        >
          <DriveIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
        </Box>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          {t("typography.confirmRideUpdate", {
            defaultValue: "Confirm Ride Update"
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("typography.reviewRideChanges", {
            defaultValue: "Please review the changes you're about to make"
          })}
        </Typography>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Driver */}
          {driverEmail && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                backgroundColor: theme.palette.action.hover,
                borderRadius: "8px"
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CarIcon
                  sx={{ fontSize: 20, color: theme.palette.text.secondary }}
                />
                <Typography variant="body2" color="text.secondary">
                  {t("driver.label", { ns: "formFieldsTranslation" })}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600}>
                {driverEmail}
              </Typography>
            </Box>
          )}

          {/* Key */}
          {keyName && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                backgroundColor: theme.palette.action.hover,
                borderRadius: "8px"
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <KeyIcon
                  sx={{ fontSize: 20, color: theme.palette.text.secondary }}
                />
                <Typography variant="body2" color="text.secondary">
                  {t("key.label", { ns: "formFieldsTranslation" })}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600}>
                {keyName}
              </Typography>
            </Box>
          )}

          {/* Ride Mode */}
          {rideModeLabel && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                backgroundColor: theme.palette.action.hover,
                borderRadius: "8px"
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("updateObjectRideForm.type")}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {rideModeLabel}
              </Typography>
            </Box>
          )}

          {/* Private Mileage */}
          {formData.privateMileage !== undefined &&
            formData.privateMileage !== "" && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 2,
                  backgroundColor: theme.palette.action.hover,
                  borderRadius: "8px"
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t("updateObjectRideForm.privateMileageKm")}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatPrivateMileage(formData.privateMileage)}
                </Typography>
              </Box>
            )}

          {/* Start Address */}
          {(formData.startAddress || formData.correctedStartAddress) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                p: 2,
                backgroundColor: theme.palette.action.hover,
                borderRadius: "8px"
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("updateObjectRideForm.rideStartAddress")}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ maxWidth: "60%", textAlign: "right" }}
              >
                {formData.startAddress || formData.correctedStartAddress}
              </Typography>
            </Box>
          )}

          {/* End Address */}
          {(formData.endAddress || formData.correctedEndAddress) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                p: 2,
                backgroundColor: theme.palette.action.hover,
                borderRadius: "8px"
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("updateObjectRideForm.rideEndAddress")}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ maxWidth: "60%", textAlign: "right" }}
              >
                {formData.endAddress || formData.correctedEndAddress}
              </Typography>
            </Box>
          )}

          {/* Comments */}
          {formData.comments && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                p: 2,
                backgroundColor: theme.palette.action.hover,
                borderRadius: "8px"
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("updateObjectRideForm.comment")}
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {formData.comments}
              </Typography>
            </Box>
          )}

          {/* Change Reason */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              p: 2,
              backgroundColor: theme.palette.warning.light,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.warning.main}`
            }}
          >
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {t("typography.reasonForChange", {
                defaultValue: "Reason for Change"
              })}
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {formData.changeReason}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button variant="outlined" onClick={onClose} theme={theme}>
          {t("typography.backToEdit", { defaultValue: "Back to Edit" })}
        </Button>
        <Button variant="contained" onClick={onConfirm} theme={theme}>
          {t("typography.confirmUpdate", { defaultValue: "Confirm Update" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateObjectRideConfirmationModal;
